import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, subDays } from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Calendar } from '@components/ui/calendar';
import { tenantToday } from '@utils/timeZone';
import { shortDate, shortDay } from '@utils/i18n';
import { PhoneDateBarProps } from './types';

const arrowClass =
  'flex h-10 w-11 shrink-0 items-center justify-center rounded-md border border-neutral-100 bg-neutral-50 text-neutral-800 disabled:opacity-50';

export const PhoneDateBar = ({
  date,
  onChange,
  disabled,
  dots,
  onMonthChange,
  onOpenChange,
}: PhoneDateBarProps) => {
  const { t } = useTranslation('app');
  const [open, setOpen] = useState(false);

  const toggle = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
    if (next) onMonthChange?.(date);
  };

  const pick = (value: Date) => {
    toggle(false);
    onChange(value);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          aria-label={t('scheduling.phone.previousDay')}
          disabled={disabled}
          onClick={() => onChange(subDays(date, 1))}
          className={arrowClass}>
          <ChevronLeft className="h-[18px] w-[18px]" />
        </button>
        <button
          type="button"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => toggle(!open)}
          className="flex h-10 min-w-0 flex-1 items-center justify-center gap-2 text-[17px] font-bold text-neutral-800">
          <CalendarDays className="h-5 w-5 shrink-0 text-primary-500" />
          <span className="truncate">
            {shortDay(date)}, {shortDate(date)}, {date.getFullYear()}
          </span>
        </button>
        <button
          type="button"
          aria-label={t('scheduling.phone.nextDay')}
          disabled={disabled}
          onClick={() => onChange(addDays(date, 1))}
          className={arrowClass}>
          <ChevronRight className="h-[18px] w-[18px]" />
        </button>
      </div>
      {open && (
        <div className="flex flex-col rounded-xl border border-neutral-200 bg-white shadow-[0_8px_24px_rgba(20,30,45,.14)]">
          <div className="px-3 pt-3">
            <button
              type="button"
              onClick={() => pick(tenantToday())}
              className="h-10 w-full rounded-lg border border-primary-100 bg-primary-50 text-sm font-semibold text-primary-600 active:bg-primary-100">
              {t('actions.today')}
            </button>
          </div>
          <div className="flex justify-center [&>div]:max-h-none">
            <Calendar
              mode="single"
              selected={date}
              defaultMonth={date}
              weekStartsOn={1}
              onSelect={(value) => value && pick(value)}
              onMonthChange={onMonthChange}
              modifiers={{ conflict: dots || [] }}
              modifiersClassNames={{
                conflict:
                  'relative after:absolute after:bottom-0.5 after:left-1/2 after:h-[5px] after:w-[5px] after:-translate-x-1/2 after:rounded-full after:bg-warning-500',
              }}
              classNames={{
                weekday: 'w-10 text-[0.8rem] font-normal text-neutral-500',
                day_button: 'h-10 w-10 rounded-full p-0 hover:bg-primary-50',
                selected: 'rounded-full bg-primary-500 text-white',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
